/**
 * Gap check across the section's RANGE OF MOTION (FXC-9009 #8).
 *
 * The per-slot gap metrics ({@link gapMetrics}) are computed at ONE operating point. But the flap
 * deploys and the flaperon deflects through a RANGE — and a rigging that looks fine when stowed can
 * CLOSE a slot, or make adjacent elements interpenetrate, partway through the deployment. A static
 * single-point gap readout misses that.
 *
 * This sweeps the MOTION knobs (flap deploy + flaperon deflection) over their effective limits — the
 * same [min,max] the Design page's fixed/free + limits panel defines (knobBounds, FXC-9009 #7) — and
 * reports, PER SLOT, the MINIMUM AND MAXIMUM gap encountered over the whole motion envelope (each with
 * the motion state where it occurs), and whether the elements ever collide (true edge interpenetration
 * via loopsCross, or a non-positive throat). The overall `status`/`minGap`/`worstSlot` are driven by the
 * MIN side only (a wide-open slot is never itself a health problem); `maxGap` exists so a caller can draw
 * the full [min,max] envelope a slot's gap sweeps through, not just its worst point. Pure: it re-derives
 * the geometry with {@link buildConfiguration} on a clone per motion sample; no backend, no mutation of
 * the live config.
 *
 * Conventions: gaps are chord-normalized (the geometry frame is x aft / y up, chord = 1).
 */
import { buildConfiguration, loopsCross } from './geometry';
import { orderByLeadingEdge } from './gapMetrics';
import { elementsOf } from './elements';
import type { HighLiftAirfoil, V2 } from './estolConfig';
import { KNOB_REGISTRY, knobBounds, type KnobConstraint } from '../foil/knobRegistry';

/** Motion knobs whose range is swept (the things that physically move during operation). */
const DEPLOY_KEY = 'flap_deploy_deg';
const FLAPERON_KEY = 'flaperon_deflection_deg';

export type MotionGapStatus = 'ok' | 'choked' | 'collision';

/** The motion axis an "animate"/range-of-motion sweep drives for a design. */
export type MotionAxis = 'deploy' | 'flaperon';

/**
 * Which motion axis "animate deploy" (and the range-of-motion envelope bar) should sweep for a
 * design, and over what range.
 *
 * The DEFAULT multi-element section deploys its vane + flaperon-derived assembly on the deploy
 * track, so the sweep drives `deployAngleDeg` over the flap-deploy limits (today's behaviour). A
 * CS-seeded plain-flap aileron carries NO vane / flaperon-derived element — its only moving surface
 * is the plain flap, which {@link buildConfiguration} rotates by its rigged `deflectionDeg` PLUS
 * `op.flaperonAngleDeg`. For that design the deploy axis is geometrically inert (nothing consumes
 * `deployAngleDeg`), so the sweep must drive the FLAPERON axis over the flaperon-deflection limits.
 * Pure — reads only the element kinds + the already-computed motion ranges.
 */
export function animateMotionAxis(
  cfg: HighLiftAirfoil,
  motionGap: Pick<MotionGapCheck, 'deployRange' | 'flaperonRange'>,
): { axis: MotionAxis; range: [number, number] } {
  const els = elementsOf(cfg);
  const hasPlainFlap = els.some((e) => e.kind === 'plain-flap');
  const hasDeployDevice = els.some((e) => e.kind === 'vane-bspline' || e.kind === 'flaperon-derived');
  if (hasPlainFlap && !hasDeployDevice) {
    return { axis: 'flaperon', range: motionGap.flaperonRange };
  }
  return { axis: 'deploy', range: motionGap.deployRange };
}

/** Health of one inter-element slot across the full motion envelope. */
export interface SlotMotionHealth {
  /** Upstream (forward) deployed-element id. */
  upstreamId: string;
  /** Downstream (aft) deployed-element id. */
  downstreamId: string;
  /** Minimum slot throat (chords) encountered anywhere in the motion envelope. */
  minGap: number;
  /** Deploy angle (deg) at which the minimum gap occurs. */
  atDeployDeg: number;
  /** Flaperon angle (deg) at which the minimum gap occurs. */
  atFlaperonDeg: number;
  /** Maximum slot throat (chords) encountered anywhere in the motion envelope. */
  maxGap: number;
  /** Deploy angle (deg) at which the maximum gap occurs. */
  atMaxDeployDeg: number;
  /** Flaperon angle (deg) at which the maximum gap occurs. */
  atMaxFlaperonDeg: number;
  /** True if the elements ever interpenetrate (crossing edges) or the throat goes non-positive. */
  collides: boolean;
  /** True if the slot never collides but the min gap drops below the warn band. */
  choked: boolean;
}

export interface MotionGapCheck {
  /** 'collision' if any slot interpenetrates; else 'choked' if any slot is below the warn band; else 'ok'. */
  status: MotionGapStatus;
  /** The worst (smallest) min-gap across all slots (chords); Infinity when there are no slots. */
  minGap: number;
  /** The slot owning {@link minGap}, or null when there are no inter-element slots. */
  worstSlot: SlotMotionHealth | null;
  /** Per-slot health, in chordwise (leading-edge) order. */
  slots: SlotMotionHealth[];
  /** The swept deploy range [min,max] (deg) — the effective limits used. */
  deployRange: [number, number];
  /** The swept flaperon range [min,max] (deg). */
  flaperonRange: [number, number];
  /** Number of motion grid points evaluated. */
  samples: number;
}

export interface MotionGapOptions {
  /** Per-knob fixed/free + limits (defaults to cfg.constraints). The deploy/flaperon bounds come from here. */
  constraints?: Record<string, KnobConstraint>;
  /** Deploy-angle grid resolution (default 9). */
  deploySteps?: number;
  /** Flaperon-angle grid resolution (default 3). 1 holds the flaperon at the range's low end. */
  flaperonSteps?: number;
  /** A slot tighter than this (chords) but not colliding is flagged 'choked' (default 0.005 c = 0.5%
   *  chord). Typical high-lift slot gaps are ~1-3% c; below ~0.5% c the slot is genuinely choked.
   *  Chosen so a retracted/stowed nest (slot legitimately closed at low deploy) is not flagged. */
  warnGap?: number;
  /** A throat at or below this (chords) counts as a collision regardless of loopsCross (default 0). */
  collideGap?: number;
}

/** Minimum distance from point `p` to the segment [a,b]. */
export function pointToSegment(p: V2, a: V2, b: V2): number {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const len2 = abx * abx + aby * aby;
  let t = len2 > 0 ? ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(p[0] - (a[0] + t * abx), p[1] - (a[1] + t * aby));
}

/**
 * Vertex-to-edge clearance between two CLOSED contours: the smallest vertex-to-EDGE distance,
 * checked both ways. Unlike a vertex-to-vertex nearest-vertex distance (which OVER-reports the
 * throat when the true minimum falls on the interior of a long edge — e.g. the ~0.15c straight cove
 * wall), this catches tight throats between coarse contours. Edge crossings are checked separately.
 * O(|a|*|b|).
 */
export function contourClearance(a: V2[], b: V2[]): number {
  let best = Infinity;
  for (let i = 0; i < a.length; i++) {
    const p = a[i];
    if (!p) continue;
    for (let j = 0; j < b.length; j++) {
      const s = b[j];
      const e = b[(j + 1) % b.length];
      if (s && e) best = Math.min(best, pointToSegment(p, s, e));
    }
  }
  for (let i = 0; i < b.length; i++) {
    const p = b[i];
    if (!p) continue;
    for (let j = 0; j < a.length; j++) {
      const s = a[j];
      const e = a[(j + 1) % a.length];
      if (s && e) best = Math.min(best, pointToSegment(p, s, e));
    }
  }
  return best;
}

/** Inclusive linear samples lo..hi (single value when n<=1 or lo==hi). */
function linspace(lo: number, hi: number, n: number): number[] {
  if (!(n > 1) || hi === lo) return [lo];
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(lo + ((hi - lo) * i) / (n - 1));
  return out;
}

/** Effective [min,max] for a motion knob (user limits if set, else the registry default range). */
function motionRange(
  key: string,
  constraints?: Record<string, KnobConstraint>,
): [number, number] {
  const def = KNOB_REGISTRY.find((k) => k.key === key);
  if (!def) return [0, 0];
  return knobBounds(def, constraints?.[key]);
}

/**
 * Sweep the motion envelope and report per-slot minimum gap + collision across the deployment.
 *
 * The deploy and flaperon angles are swept over their effective limits as a grid; at each motion
 * state the geometry is rebuilt and every consecutive inter-element slot's throat and edge crossing
 * state are measured.
 */
export function motionGapCheck(
  cfg: HighLiftAirfoil,
  opts: MotionGapOptions = {},
): MotionGapCheck {
  const deploySteps = opts.deploySteps ?? 9;
  const flaperonSteps = opts.flaperonSteps ?? 3;
  const warnGap = opts.warnGap ?? 0.005;
  const collideGap = opts.collideGap ?? 0;
  const constraints = opts.constraints ?? cfg.constraints;

  const deployRange = motionRange(DEPLOY_KEY, constraints);
  const flaperonRange = motionRange(FLAPERON_KEY, constraints);
  const deploys = linspace(deployRange[0], deployRange[1], deploySteps);
  const flaperons = linspace(flaperonRange[0], flaperonRange[1], Math.max(1, flaperonSteps));

  // Per-slot accumulator keyed by the element-pair identity (stable across re-ordering).
  const acc = new Map<string, SlotMotionHealth>();
  let samples = 0;

  for (const deployDeg of deploys) {
    for (const flaperonDeg of flaperons) {
      const clone = structuredClone(cfg);
      clone.operation.deployAngleDeg = deployDeg;
      clone.operation.flaperonAngleDeg = flaperonDeg;
      const conf = buildConfiguration(clone);
      const ordered = orderByLeadingEdge(
        conf.elements.map((e) => ({ id: e.id, contour: e.contour })),
      );
      samples++;
      for (let i = 0; i + 1 < ordered.length; i++) {
        const up = ordered[i];
        const dn = ordered[i + 1];
        if (!up || !dn) continue;
        const gap = contourClearance(up.contour, dn.contour);
        const collides = gap <= collideGap || loopsCross(up.contour, dn.contour);
        // Order-independent slot identity so a leading-edge re-ordering mid-sweep (the general
        // N-element case) can't split one physical slot into two half-counted accumulator entries.
        const key = up.id < dn.id ? `${up.id}|${dn.id}` : `${dn.id}|${up.id}`;
        const cur = acc.get(key);
        if (!cur) {
          acc.set(key, {
            upstreamId: up.id,
            downstreamId: dn.id,
            minGap: gap,
            atDeployDeg: deployDeg,
            atFlaperonDeg: flaperonDeg,
            maxGap: gap,
            atMaxDeployDeg: deployDeg,
            atMaxFlaperonDeg: flaperonDeg,
            collides,
            choked: false,
          });
        } else {
          cur.upstreamId = up.id;
          cur.downstreamId = dn.id;
          if (gap < cur.minGap) {
            cur.minGap = gap;
            cur.atDeployDeg = deployDeg;
            cur.atFlaperonDeg = flaperonDeg;
          }
          if (gap > cur.maxGap) {
            cur.maxGap = gap;
            cur.atMaxDeployDeg = deployDeg;
            cur.atMaxFlaperonDeg = flaperonDeg;
          }
          cur.collides = cur.collides || collides;
        }
      }
    }
  }

  const slots = [...acc.values()];
  for (const s of slots) s.choked = !s.collides && s.minGap < warnGap;

  let worstSlot: SlotMotionHealth | null = null;
  for (const s of slots) if (!worstSlot || s.minGap < worstSlot.minGap) worstSlot = s;

  const status: MotionGapStatus = slots.some((s) => s.collides)
    ? 'collision'
    : slots.some((s) => s.choked)
      ? 'choked'
      : 'ok';

  return {
    status,
    minGap: worstSlot ? worstSlot.minGap : Infinity,
    worstSlot,
    slots,
    deployRange,
    flaperonRange,
    samples,
  };
}
